import { useEffect, useId, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import PropTypes from "prop-types";
import { Loader2 } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { NativeSelect } from "@/components/ui/native-select";
import { Pagination } from "@/components/ui/pagination";
import { cn } from "@/lib/utils";
import PageHeader from "../PageHeader";
import usePageParam from "../../hooks/usePageParam";
import { investingApi } from "../../services/api";
import { apiErrorMessage } from "../../services/apiError";
import UntrustedMarkdown from "./UntrustedMarkdown";
import { formatDay, thesisPath } from "./lib/format";

// The API's page size for theses (constants.py THESIS_PAGE_SIZE).
const PAGE_SIZE = 100;
const STATUSES = new Set(["active", "retired"]);

function scoredLine(count) {
  if (!count) return "No company scored yet";
  return `Scores ${count.toLocaleString("en-US")} ${count === 1 ? "company" : "companies"}`;
}

function ThesisItem({ thesis }) {
  const changed = formatDay(thesis.updated_at);
  return (
    <li className="rounded-lg border bg-card p-4">
      <h2 className="m-0 text-base font-semibold wrap-anywhere">
        <Link
          to={thesisPath(thesis.slug)}
          className="inline-flex min-h-11 items-center underline-offset-2 hover:underline md:min-h-0"
        >
          {thesis.title}
        </Link>
        {thesis.status === "retired" && (
          <>
            {" "}
            <Badge variant="outline" className="align-middle" title="Retired by a person: it takes no new scores">
              Retired
            </Badge>
          </>
        )}
      </h2>
      <div className="mt-1 text-sm">
        {thesis.claim ? (
          <UntrustedMarkdown body={thesis.claim} />
        ) : (
          <p className="m-0 text-muted-foreground">No claim written yet.</p>
        )}
      </div>
      <p className="mt-2 mb-0 text-sm text-muted-foreground">
        {scoredLine(thesis.scored_companies)}
        {changed ? ` · Changed ${changed}` : ""}
      </p>
    </li>
  );
}

ThesisItem.propTypes = {
  thesis: PropTypes.shape({
    slug: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    claim: PropTypes.string,
    scored_companies: PropTypes.number,
    updated_at: PropTypes.string,
  }).isRequired,
};

/**
 * Theses (spec §5.3, walkthrough 5): the claims about a fast-changing
 * world that companies are scored against, 100 a page, by slug. Each
 * names its claim (agent-written markdown, rendered untrusted — D8), how
 * many companies it scores, and when it last changed; its page has the
 * whole argument, its history and its companies.
 *
 * The status filter and the page live in the URL; a filter change drops
 * the page in the same write and replaces the history entry, paging pushes
 * one, and a page past the end heals to page 1. Only the newest request
 * lands.
 */
function InvestingTheses() {
  const id = useId();
  const [searchParams, setSearchParams] = useSearchParams();
  const [page, setPage] = usePageParam();
  const raw = searchParams.get("status") || "";
  const status = STATUSES.has(raw) ? raw : "";

  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadTick, setReloadTick] = useState(0);

  const requestRef = useRef(0);
  useEffect(() => {
    const request = ++requestRef.current;
    const isLatest = () => request === requestRef.current;
    setLoading(true);
    setError(null);
    investingApi.theses.list({ ...(status ? { status } : {}), page }).then(
      ({ data }) => {
        if (!isLatest()) return;
        setRows(data?.results || []);
        setCount(data?.count || 0);
        setLoading(false);
      },
      (err) => {
        if (!isLatest()) return;
        if (err?.response?.status === 404 && page > 1) {
          setPage(1, { replace: true });
          return;
        }
        setError(err);
        setLoading(false);
      },
    );
  }, [status, page, setPage, reloadTick]);

  const setStatus = (value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set("status", value);
    else next.delete("status");
    next.delete("page");
    setSearchParams(next, { replace: true });
  };

  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));
  const summary = `${count.toLocaleString("en-US")} ${count === 1 ? "thesis" : "theses"}${
    totalPages > 1 ? ` — page ${page} of ${totalPages}` : ""
  }.`;

  return (
    <div className="page-container mt-6 pt-4">
      <div className="max-w-4xl">
        <PageHeader
          title="Theses"
          subtitle="The claims about a fast-changing world that every company is scored against, from −2 to +2. Agents write and revise them on the board; only you retire one."
        />

        <div className="mb-3 flex flex-wrap items-end gap-3">
          <div className="flex min-w-48 flex-col gap-1">
            <Label htmlFor={`${id}-status`}>Status</Label>
            <NativeSelect
              id={`${id}-status`}
              value={status}
              className="h-11 md:h-9"
              aria-describedby={`${id}-status-help`}
              onChange={(event) => setStatus(event.target.value)}
            >
              <option value="">All theses</option>
              <option value="active">Active</option>
              <option value="retired">Retired</option>
            </NativeSelect>
            <p id={`${id}-status-help`} className="sr-only">
              Active theses take new scores; retired ones keep the scores they gave.
            </p>
          </div>
          <p role="status" className="m-0 min-h-11 content-center text-sm text-muted-foreground md:min-h-9">
            {loading ? "Loading theses…" : error || count === 0 ? "" : summary}
          </p>
        </div>

        {error ? (
          <Alert variant="destructive" role="alert">
            <AlertDescription className="flex flex-wrap items-center gap-2">
              <span>{apiErrorMessage(error, "Couldn't load the theses.")}</span>{" "}
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-11 md:h-8"
                onClick={() => setReloadTick((tick) => tick + 1)}
              >
                Retry
              </Button>
            </AlertDescription>
          </Alert>
        ) : loading && rows.length === 0 ? (
          <div className="py-12 text-center">
            <Loader2 className="inline size-6 animate-spin text-muted-foreground" aria-hidden="true" />
          </div>
        ) : !loading && count === 0 ? (
          <div className="rounded-lg border bg-card p-4">
            <p className="m-0">
              {status
                ? `No ${status} theses.`
                : "No theses yet. They arrive when an agent works a board task “Investing: thesis — <name>”: a claim, why it would be true, who wins and who loses, and what would prove it wrong. Research then scores each company against the theses that apply."}
            </p>
          </div>
        ) : (
          <ul
            aria-label="Theses"
            aria-busy={loading || undefined}
            className={cn("m-0 flex list-none flex-col gap-3 p-0", loading && "opacity-60")}
          >
            {rows.map((thesis) => (
              <ThesisItem key={thesis.slug} thesis={thesis} />
            ))}
          </ul>
        )}

        {!error && totalPages > 1 && (
          <Pagination className="my-6" page={page} total={totalPages} onChange={(next) => setPage(next)} showEdges />
        )}
      </div>
    </div>
  );
}

export default InvestingTheses;
